import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  SupportSavedReply,
  useDeleteSavedReplyMutation,
  useGetSavedRepliesQuery,
  useSaveSavedReplyMutation,
} from "../../slices/SupportSlice";
import { errorText, MAX_REPLY } from "./support.utils";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/** Help desk › Saved replies (owner 2026-10-25): ready-made answers, inserted with one click; the AI builds on them. */
export const SavedRepliesPage = () => {
  const { data, isLoading } = useGetSavedRepliesQuery();
  const [editing, setEditing] = useState<string | "new" | null>(null);
  if (isLoading) return <LottieLoading />;
  const replies = data || [];
  return (
    <div className={PAGE}>
      <div className={classNames(PAGE_INNER, "max-w-[860px]")}>
        <Link
          to={"/support"}
          className={
            "text-[13px] font-semibold text-[#6B7185] hover:text-[#6B4EFF]"
          }>
          ‹ Help desk
        </Link>
        <div
          className={
            "mb-4 mt-2 flex flex-wrap items-end justify-between gap-3"
          }>
          <div>
            <h1
              className={
                "mb-1 text-[24px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
              }>
              Saved replies
            </h1>
            <p className={"text-[14px] text-[#5D6475]"}>
              Answers you write once and insert with one click. The AI also uses
              them when it drafts a reply.
            </p>
          </div>
          {editing !== "new" && (
            <button
              type={"button"}
              className={PRIMARY_BUTTON}
              onClick={() => setEditing("new")}>
              New saved reply
            </button>
          )}
        </div>
        {editing === "new" && <ReplyForm onClose={() => setEditing(null)} />}
        {replies.length === 0 && editing !== "new" ? (
          <div
            className={
              "rounded-[14px] border border-[#EDEFF3] bg-white px-4 py-12 text-center"
            }>
            <div className={"text-[15px] font-semibold text-[#1D2C3B]"}>
              No saved replies yet
            </div>
            <div className={"mt-1 text-sm text-[#8A90A2]"}>
              Good first ones: how to reset a password, how to import clients,
              how to add a team member.
            </div>
          </div>
        ) : (
          <div className={"flex flex-col gap-2.5"}>
            {replies.map((reply) =>
              editing === reply.id ? (
                <ReplyForm
                  key={reply.id}
                  reply={reply}
                  onClose={() => setEditing(null)}
                />
              ) : (
                <ReplyCard
                  key={reply.id}
                  reply={reply}
                  onEdit={() => setEditing(reply.id)}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ReplyCard = ({
  reply,
  onEdit,
}: {
  reply: SupportSavedReply;
  onEdit: () => void;
}) => {
  const [remove, { isLoading }] = useDeleteSavedReplyMutation();
  const [asking, setAsking] = useState(false);
  return (
    <div
      className={"rounded-[14px] border border-[#EDEFF3] bg-white px-4 py-3.5"}>
      <div className={"flex flex-wrap items-center justify-between gap-2"}>
        <div className={"text-[14.5px] font-bold text-[#1F2433]"}>
          {reply.title}
        </div>
        <div className={"flex gap-2"}>
          <button type={"button"} className={SMALL_BUTTON} onClick={onEdit}>
            Edit
          </button>
          {asking ? (
            <>
              <button
                type={"button"}
                disabled={isLoading}
                className={classNames(SMALL_BUTTON, "text-[#B42318]")}
                onClick={() =>
                  remove(reply.id)
                    .unwrap()
                    .catch((e) =>
                      toast(errorText(e, "Couldn't delete it."), {
                        type: "error",
                      }),
                    )
                }>
                Yes, delete
              </button>
              <button
                type={"button"}
                className={SMALL_BUTTON}
                onClick={() => setAsking(false)}>
                Keep
              </button>
            </>
          ) : (
            <button
              type={"button"}
              className={classNames(SMALL_BUTTON, "text-[#B42318]")}
              onClick={() => setAsking(true)}>
              Delete
            </button>
          )}
        </div>
      </div>
      <div
        className={
          "mt-1.5 line-clamp-3 whitespace-pre-wrap text-[13.5px] text-[#3A4256]"
        }>
        {reply.body}
      </div>
    </div>
  );
};

export const ReplyForm = ({
  reply,
  onClose,
}: {
  reply?: SupportSavedReply;
  onClose: () => void;
}) => {
  const [title, setTitle] = useState(reply?.title || "");
  const [body, setBody] = useState(reply?.body || "");
  const [save, { isLoading }] = useSaveSavedReplyMutation();
  const problem = !title.trim()
    ? "Give the saved reply a name."
    : title.trim().length > 120
    ? "The name can be at most 120 characters."
    : !body.trim()
    ? "Write the reply text."
    : "";
  return (
    <div
      className={
        "mb-2.5 flex flex-col gap-2.5 rounded-[14px] border border-[#D9D0FF] bg-white px-4 py-4"
      }>
      <label
        className={"flex flex-col gap-1 text-[13px] font-bold text-[#3A4256]"}>
        Name
        <input
          value={title}
          maxLength={120}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={"e.g. Reset sign-in"}
          className={
            "h-10 rounded-lg border border-[#DCE2EA] px-3 text-[14px] font-normal"
          }
        />
      </label>
      <label
        className={"flex flex-col gap-1 text-[13px] font-bold text-[#3A4256]"}>
        Reply
        <textarea
          value={body}
          maxLength={MAX_REPLY}
          rows={8}
          onChange={(e) => setBody(e.target.value)}
          placeholder={"Hi,\n\n…\n\nThe ProCharted team"}
          className={
            "resize-y rounded-lg border border-[#DCE2EA] px-3 py-2.5 text-[14px] font-normal leading-relaxed"
          }
        />
      </label>
      <div className={"flex flex-wrap gap-2"}>
        <button
          type={"button"}
          disabled={!!problem || isLoading}
          title={problem || undefined}
          className={PRIMARY_BUTTON}
          onClick={() =>
            save({ id: reply?.id, title, body })
              .unwrap()
              .then(onClose)
              .catch((e) =>
                toast(errorText(e, "Couldn't save it."), { type: "error" }),
              )
          }>
          {isLoading ? "Saving…" : "Save"}
        </button>
        <button type={"button"} className={SMALL_BUTTON} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
};
